// Advanced Analytics Engine - Production Optimized
import { analyticsConfig } from '../config.js';

export class AnalyticsEngine {
    constructor() {
        this.sessionId = this.generateSessionId();
        this.events = [];
        this.startTime = Date.now();
        this.trackingLevel = 'limited'; // Start with limited until consent
        this.isInitialized = false;
        this.jitsuLoaded = false;
        this.queue = [];
        this.fallbackMode = false;
        this.init();
    }

    async init() {
        try {
            // Wait for Jitsu to load
            await this.waitForJitsu();
            this.jitsuLoaded = true;
            console.log('Jitsu loaded successfully');
            
            // Set up session
            this.setupSession();
            
        } catch (error) {
            console.warn('Jitsu failed to load, creating fallback...', error.message);
            this.fallbackMode = true;
            this.setupFallbackAnalytics();
        }
        
        // Initialize tracking regardless of Jitsu status
        this.setupEngagementTracking();
        this.setupErrorTracking();
        this.setupPerformanceTracking();
        
        this.isInitialized = true;
        
        // Process queued events
        this.processQueue();
    }

    setupFallbackAnalytics() {
        // Create a minimal fallback tracking system
        window.jitsu = {
            identify: (sessionId, data) => {
                console.log('Fallback Jitsu identify:', sessionId, data);
                // Store session data locally
                localStorage.setItem('fallback_session', JSON.stringify({
                    sessionId,
                    data,
                    timestamp: new Date().toISOString()
                }));
            },
            track: (eventName, data) => {
                console.log('Fallback Jitsu track:', eventName, data);
                // Store events locally for potential later sync
                const events = JSON.parse(localStorage.getItem('fallback_events') || '[]');
                events.push({
                    event: eventName,
                    data,
                    timestamp: new Date().toISOString()
                });
                // Keep only last 100 events to prevent storage overflow
                if (events.length > 100) {
                    events.splice(0, events.length - 100);
                }
                localStorage.setItem('fallback_events', JSON.stringify(events));
                
                // Also send to console for debugging
                console.log(`Event tracked successfully: ${eventName}`);
            },
            page: (data) => {
                console.log('Fallback Jitsu page:', data);
            }
        };
        
        // Setup session in fallback mode
        this.setupSession();
    }

    async waitForJitsu(timeout = 5000) {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            
            const checkJitsu = () => {
                if (window.jitsu && typeof window.jitsu.track === 'function') {
                    resolve();
                } else if (Date.now() - startTime > timeout) {
                    reject(new Error('Jitsu failed to load'));
                } else {
                    setTimeout(checkJitsu, 100);
                }
            };
            
            checkJitsu();
        });
    }

    generateSessionId() {
        const timestamp = Date.now().toString(36);
        const randomStr = Math.random().toString(36).substr(2, 9);
        return `session_${timestamp}_${randomStr}`;
    }

    setupSession() {
        if (!window.jitsu) return;
        
        const sessionData = {
            sessionId: this.sessionId,
            domain: window.location.hostname,
            sessionStart: new Date(this.startTime).toISOString(),
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            screenResolution: `${screen.width}x${screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
            platform: navigator.platform
        };
        
        try {
            window.jitsu.identify(this.sessionId, sessionData);
        } catch (error) {
            console.error('Failed to set up session:', error);
        }
    }

    async trackPageView() {
        const pageData = {
            title: document.title,
            url: window.location.href,
            path: window.location.pathname,
            domain: window.location.hostname,
            referrer: document.referrer,
            sessionId: this.sessionId,
            timestamp: new Date().toISOString()
        };
        
        try {
            if (window.jitsu && window.jitsu.page) {
                await window.jitsu.page(pageData);
            }
            console.log('Page view tracked successfully');
        } catch (error) {
            console.error('Failed to track page view:', error);
        }
    }

    async trackEvent(category, action, label, value = null) {
        const eventData = {
            category,
            action,
            label,
            value,
            domain: window.location.hostname,
            sessionId: this.sessionId,
            timestamp: new Date().toISOString(),
            pageUrl: window.location.href,
            timeOnPage: this.getTimeOnPage(),
            scrollDepth: this.getCurrentScrollDepth(),
            visitorIntent: this.determineVisitorIntent()
        };
        
        await this.track(`${category}_${action}`, eventData);
        
        // Store event
        this.events.push({
            type: 'event',
            timestamp: Date.now(),
            data: eventData
        });
        
        // Check for high-intent actions
        if (this.isHighIntentAction(category, action)) {
            await this.trackHighIntent(category, action, label);
        }
    }

    async track(eventName, data = {}) {
        // Check tracking level
        if (this.trackingLevel === 'none') return;
        
        // Filter data based on tracking level
        const filteredData = this.filterDataByTrackingLevel(data);
        
        // Add to queue if not initialized
        if (!this.isInitialized) {
            this.queue.push({ eventName, data: filteredData });
            return;
        }
        
        try {
            if (window.jitsu && window.jitsu.track) {
                await window.jitsu.track(eventName, filteredData);
                console.debug('Analytics event:', eventName, filteredData);
            }
        } catch (error) {
            console.error('Failed to track event:', error);
            this.handleAnalyticsError(error);
        }
    }

    filterDataByTrackingLevel(data) {
        if (this.trackingLevel === 'full') {
            return data;
        } else if (this.trackingLevel === 'limited') {
            // Remove PII and detailed data
            const { userAgent, screenResolution, platform, ...limitedData } = data;
            return limitedData;
        }
        return {};
    }

    processQueue() {
        while (this.queue.length > 0) {
            const { eventName, data } = this.queue.shift();
            this.track(eventName, data);
        }
    }

    isHighIntentAction(category, action) {
        const highIntentActions = [
            'Contact_FormSubmit',
            'Contact_FormFieldFocus',
            'Content_DeepScroll',
            'Engagement_LongSession',
            'Navigation_ExternalLink'
        ];
        
        return highIntentActions.includes(`${category}_${action}`);
    }

    async trackHighIntent(category, action, label) {
        const highIntentData = {
            category,
            action,
            label,
            intent: 'high',
            domain: window.location.hostname,
            sessionId: this.sessionId,
            timestamp: new Date().toISOString(),
            timeOnPage: this.getTimeOnPage(),
            scrollDepth: this.getCurrentScrollDepth(),
            totalEvents: this.events.length,
            engagementScore: this.calculateEngagementScore()
        };
        
        await this.track('high_intent_action', highIntentData);
        console.log('High intent action tracked:', { category, action, label });
    }

    setupEngagementTracking() {
        // Time tracking
        setInterval(() => {
            const timeOnPage = this.getTimeOnPage();
            if (timeOnPage % 30 === 0 && timeOnPage > 0) {
                this.trackEvent('Engagement', 'TimeOnSite', `${timeOnPage}s`);
            }
        }, 1000);

        // Scroll tracking
        this.setupScrollTracking();
        
        // Click tracking
        this.setupClickTracking();
        
        // Visibility tracking
        this.setupVisibilityTracking();
    }

    setupScrollTracking() {
        let lastScrollDepth = 0;
        const scrollThresholds = [25, 50, 75, 90, 100];
        const scrollStopTimeout = 2000;
        let scrollTimer;

        const trackScroll = () => {
            const currentDepth = this.getCurrentScrollDepth();
            
            // Clear previous scroll stop timer
            clearTimeout(scrollTimer);
            
            // Track scroll milestones
            scrollThresholds.forEach(threshold => {
                if (currentDepth >= threshold && lastScrollDepth < threshold) {
                    this.trackEvent('Engagement', 'Scroll', `${threshold}%`);
                    
                    // Track deep scroll as high intent
                    if (threshold >= 75) {
                        this.trackEvent('Content', 'DeepScroll', `${threshold}%`);
                    }
                }
            });
            
            lastScrollDepth = currentDepth;
            
            // Set timer for scroll stop
            scrollTimer = setTimeout(() => {
                this.trackEvent('Engagement', 'ScrollStop', `${Math.round(currentDepth)}%`);
            }, scrollStopTimeout);
        };

        // Throttle scroll events
        let scrollTimeout;
        window.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(trackScroll, 100);
        });
    }

    setupClickTracking() {
        document.addEventListener('click', (e) => {
            const target = e.target.closest('a, button, [data-track]');
            if (!target) return;
            
            const trackingData = {
                tagName: target.tagName,
                text: target.textContent?.trim().substring(0, 50),
                href: target.href,
                className: target.className,
                id: target.id,
                dataTrack: target.getAttribute('data-track')
            };
            
            // Track specific click types
            if (target.tagName === 'A') {
                if (target.href?.includes('#')) {
                    this.trackEvent('Navigation', 'AnchorClick', target.href);
                } else if (target.href?.startsWith('http') && !target.href.includes(window.location.hostname)) {
                    this.trackEvent('Navigation', 'ExternalLink', target.href);
                }
            } else if (target.tagName === 'BUTTON') {
                this.trackEvent('UI', 'ButtonClick', trackingData.text);
            }
            
            // Custom tracking
            if (trackingData.dataTrack) {
                const [category, action, label] = trackingData.dataTrack.split(':');
                this.trackEvent(category, action, label);
            }
        });
    }

    setupVisibilityTracking() {
        // Track when page becomes visible/hidden
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.trackEvent('Engagement', 'TabVisible', window.location.pathname);
            } else {
                this.trackEvent('Engagement', 'TabHidden', window.location.pathname);
            }
        });
        
        // Track when user is about to leave
        window.addEventListener('beforeunload', () => {
            this.trackEvent('Engagement', 'PageUnload', window.location.pathname);
        });
    }

    getTimeOnPage() {
        return Math.floor((Date.now() - this.startTime) / 1000);
    }

    getCurrentScrollDepth() {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        if (documentHeight <= windowHeight) return 100;
        
        const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;
        return Math.min(Math.round(scrollPercent), 100);
    }

    determineVisitorIntent() {
        const timeOnPage = this.getTimeOnPage();
        const scrollDepth = this.getCurrentScrollDepth();
        const eventCount = this.events.length;
        
        if (timeOnPage > 120 && scrollDepth > 75) return 'high';
        if (timeOnPage > 60 && scrollDepth > 50) return 'medium';
        if (timeOnPage > 30 && eventCount > 3) return 'medium';
        return 'low';
    }

    setupErrorTracking() {
        // JavaScript errors
        window.addEventListener('error', (e) => {
            this.trackEvent('Error', 'JavaScript', e.message, {
                filename: e.filename,
                lineno: e.lineno,
                colno: e.colno,
                stack: e.error?.stack
            });
        });
        
        // Promise rejections
        window.addEventListener('unhandledrejection', (e) => {
            this.trackEvent('Error', 'UnhandledRejection', e.reason?.message || e.reason);
        });
        
        // Resource loading errors
        window.addEventListener('error', (e) => {
            if (e.target !== window) {
                this.trackEvent('Error', 'ResourceLoad', e.target.src || e.target.href);
            }
        }, true);
    }

    setupPerformanceTracking() {
        // Wait for page load
        if (document.readyState === 'complete') {
            this.trackPerformanceMetrics();
        } else {
            window.addEventListener('load', () => {
                setTimeout(() => this.trackPerformanceMetrics(), 0);
            });
        }
        
        // Track Core Web Vitals
        this.trackCoreWebVitals();
    }

    trackPerformanceMetrics() {
        if (!window.performance) return;
        
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation) {
            this.trackEvent('Performance', 'LoadTime', 'ms', Math.round(navigation.loadEventEnd - navigation.loadEventStart));
            this.trackEvent('Performance', 'DOMContentLoaded', 'ms', Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart));
        }
        
        // Track resource timings
        const resources = performance.getEntriesByType('resource');
        const slowResources = resources.filter(r => r.duration > 1000);
        if (slowResources.length > 0) {
            this.trackEvent('Performance', 'SlowResource', 'count', slowResources.length);
        }
    }

    trackCoreWebVitals() {
        // Largest Contentful Paint (LCP)
        if ('PerformanceObserver' in window) {
            try {
                new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    const lastEntry = entries[entries.length - 1];
                    this.trackEvent('Performance', 'LCP', 'ms', Math.round(lastEntry.renderTime || lastEntry.loadTime));
                }).observe({ entryTypes: ['largest-contentful-paint'] });
            } catch (e) {
                console.debug('LCP tracking not supported');
            }
        
            // First Input Delay (FID)
            try {
                new PerformanceObserver((list) => {
                    const entries = list.getEntries();
                    entries.forEach(entry => {
                        this.trackEvent('Performance', 'FID', 'ms', Math.round(entry.processingStart - entry.startTime));
                    });
                }).observe({ entryTypes: ['first-input'] });
            } catch (e) {
                console.debug('FID tracking not supported');
            }
        
            // Cumulative Layout Shift (CLS)
            try {
                let clsValue = 0;
                new PerformanceObserver((list) => {
                    list.getEntries().forEach(entry => {
                        if (!entry.hadRecentInput) {
                            clsValue += entry.value;
                        }
                    });
                }).observe({ entryTypes: ['layout-shift'] });
                
                // Report CLS on page hide
                document.addEventListener('visibilitychange', () => {
                    if (document.visibilityState === 'hidden') {
                        this.trackEvent('Performance', 'CLS', 'score', Math.round(clsValue * 1000) / 1000);
                    }
                });
            } catch (e) {
                console.debug('CLS tracking not supported');
            }
        }
    }

    calculateEngagementScore() {
        const timeScore = Math.min(this.getTimeOnPage() / 300, 1) * 30;
        const scrollScore = this.getCurrentScrollDepth() / 100 * 30;
        const eventScore = Math.min(this.events.length / 20, 1) * 40;
        
        return Math.round(timeScore + scrollScore + eventScore);
    }

    storeConversionEvent(category, action) {
        const conversions = JSON.parse(localStorage.getItem('conversions') || '[]');
        conversions.push({
            category,
            action,
            timestamp: new Date().toISOString(),
            sessionId: this.sessionId
        });
        localStorage.setItem('conversions', JSON.stringify(conversions));
    }

    enableFullTracking() {
        this.trackingLevel = 'full';
        this.trackEvent('Privacy', 'TrackingLevel', 'Full');
    }

    enableLimitedTracking() {
        this.trackingLevel = 'limited';
        this.trackEvent('Privacy', 'TrackingLevel', 'Limited');
    }

    disableTracking() {
        this.trackingLevel = 'none';
        this.trackEvent('Privacy', 'TrackingLevel', 'None');
    }

    handleAnalyticsError(error) {
        console.error('Analytics error:', error);
        
        // Store errors for later reporting
        const errors = JSON.parse(sessionStorage.getItem('analytics_errors') || '[]');
        errors.push({
            message: error.message,
            timestamp: new Date().toISOString(),
            url: window.location.href
        });
        sessionStorage.setItem('analytics_errors', JSON.stringify(errors));
    }

    // Method to sync fallback events when connection is restored
    async syncFallbackEvents() {
        if (!this.fallbackMode) return;
        
        const events = JSON.parse(localStorage.getItem('fallback_events') || '[]');
        if (events.length === 0) return;
        
        console.log(`Attempting to sync ${events.length} fallback events...`);
        
        // Try to send events to server or external service
        try {
            // This would need to be implemented based on your backend
            // For now, just log that we would sync
            console.log('Fallback events ready for sync:', events);
            
            // Clear synced events
            localStorage.removeItem('fallback_events');
        } catch (error) {
            console.error('Failed to sync fallback events:', error);
        }
    }
}

// Export singleton instance
export const analytics = new AnalyticsEngine();
export default analytics;